import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Filter, User2, Clock, ScrollText, Loader2 } from "lucide-react";

const ACTION_LABEL = {
  "admin.create": "Nuovo admin",
  "admin.update": "Admin aggiornato",
  "admin.delete": "Admin eliminato",
  "supplier.create": "Nuovo fornitore",
  "supplier.update": "Fornitore aggiornato",
  "supplier.delete": "Fornitore eliminato",
  "product.create": "Nuovo prodotto",
  "product.update": "Prodotto aggiornato",
  "product.delete": "Prodotto eliminato",
  "order.update": "Ordine aggiornato",
  "order.action": "Azione ordine",
  "settings.supplier_discount": "Sconto fornitori",
  "settings.meta_pixel": "Meta Pixel",
};

const actionColor = (a) => {
  if (a.endsWith(".delete")) return "text-rose-600 bg-rose-50 border-rose-200";
  if (a.endsWith(".create")) return "text-emerald-600 bg-emerald-50 border-emerald-200";
  if (a.startsWith("settings.")) return "text-violet-600 bg-violet-50 border-violet-200";
  if (a.startsWith("order.")) return "text-amber-700 bg-amber-50 border-amber-200";
  return "text-xd-text bg-white border-xd-border";
};

const formatWhen = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function AdminActivityLog() {
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 100;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/activity-log", { params: { action: action || undefined, actor: actor || undefined, limit, skip } });
      setItems(r.data.items || []);
      setActions(r.data.actions || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Solo il super admin può vedere il log attività");
      else toast.error("Errore caricamento log");
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [action, actor, skip]);

  const canPrev = skip > 0;
  const canNext = skip + items.length < total;
  const rangeLabel = useMemo(() => `${total === 0 ? 0 : skip + 1}-${skip + items.length} di ${total}`, [skip, items.length, total]);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-semibold flex items-center gap-2" data-testid="activity-log-title">
              <ScrollText className="w-5 h-5 text-xd-cyanHover" /> Log Attività Admin
            </h1>
            <p className="text-sm text-xd-muted mt-1">Audit trail di ogni azione eseguita dagli amministratori.</p>
          </div>
          <button type="button" onClick={load} disabled={loading} data-testid="activity-log-refresh" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-xd-border bg-white hover:border-xd-cyan text-sm disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Aggiorna
          </button>
        </div>

        <div className="bg-white border border-xd-border rounded-xl p-4 mb-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-xd-muted mb-1"><Filter className="w-3 h-3 inline mr-1" /> Azione</label>
            <select value={action} onChange={(e) => { setAction(e.target.value); setSkip(0); }} data-testid="activity-log-filter-action" className="w-full h-10 rounded-lg border border-xd-border bg-white px-3 text-sm">
              <option value="">Tutte le azioni</option>
              {actions.map((a) => (
                <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-xd-muted mb-1"><User2 className="w-3 h-3 inline mr-1" /> Autore (email)</label>
            <input value={actor} onChange={(e) => { setActor(e.target.value); setSkip(0); }} placeholder="es. mauro@…" data-testid="activity-log-filter-actor" className="w-full h-10 rounded-lg border border-xd-border bg-white px-3 text-sm" />
          </div>
          <div className="text-xs text-xd-muted ml-auto" data-testid="activity-log-range">{rangeLabel}</div>
        </div>

        <div className="bg-white border border-xd-border rounded-xl overflow-hidden">
          <div className="grid grid-cols-[180px_170px_1fr_1fr] text-[11px] uppercase tracking-widest font-mono-tech text-xd-muted bg-xd-light border-b border-xd-border">
            <div className="px-4 py-3">Quando</div>
            <div className="px-4 py-3">Azione</div>
            <div className="px-4 py-3">Autore</div>
            <div className="px-4 py-3">Target / Dettagli</div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-xd-muted"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-xd-muted" data-testid="activity-log-empty">Nessuna attività registrata.</div>
          ) : (
            items.map((it) => (
              <div key={it.id} data-testid={`activity-log-row-${it.id}`} className="grid grid-cols-[180px_170px_1fr_1fr] items-start border-b border-xd-border last:border-b-0 text-sm hover:bg-xd-light/40">
                <div className="px-4 py-3 text-xd-muted flex items-center gap-1"><Clock className="w-3 h-3" /> {formatWhen(it.created_at)}</div>
                <div className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-mono-tech uppercase tracking-wider ${actionColor(it.action)}`}>{ACTION_LABEL[it.action] || it.action}</span>
                </div>
                <div className="px-4 py-3">
                  <div className="font-medium text-xd-text">{it.actor_email || "-"}</div>
                  <div className="text-[11px] text-xd-muted font-mono-tech">{(it.actor_id || "").slice(0, 8)}</div>
                </div>
                <div className="px-4 py-3">
                  <div className="text-xd-text break-all">{it.target_email || <span className="text-xd-muted">{(it.target_id || "").slice(0, 8) || "—"}</span>}</div>
                  {it.details && <div className="text-[11px] text-xd-muted mt-0.5 break-all">{it.details}</div>}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between mt-4 text-sm">
          <button type="button" onClick={() => setSkip(Math.max(0, skip - limit))} disabled={!canPrev || loading} data-testid="activity-log-prev" className="inline-flex items-center h-9 px-4 rounded-lg border border-xd-border bg-white hover:border-xd-cyan disabled:opacity-40">← Precedenti</button>
          <div className="text-xs text-xd-muted">{rangeLabel}</div>
          <button type="button" onClick={() => setSkip(skip + limit)} disabled={!canNext || loading} data-testid="activity-log-next" className="inline-flex items-center h-9 px-4 rounded-lg border border-xd-border bg-white hover:border-xd-cyan disabled:opacity-40">Successivi →</button>
        </div>
      </div>
    </AdminLayout>
  );
}
